import React, { useState } from 'react';
import { MODULES } from '../moduleRegistry';
import { moveScreens } from '../api/connectionRegistryApi';
import { apiErrorMessage } from '../utils/auditUser';
import { useScreenPlacement, setPlacement } from '../utils/screenPlacement';
import { useCanManageSettings } from '../utils/screenAccess';
import { useToast } from '../utils/useToast';
import Toast from '../components/Toast';
import Switch from '../components/Switch';

const ROW = {
  display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
  borderBottom: '1px solid #F3F4F6', fontSize: '13px', color: '#111827',
};

// Open to admins and anyone with Supporting Tools access: every Supporting Tools screen once, each with a "Make It
// Live" switch. On puts the screen in Supporting Tools Live, off returns it to
// Supporting Tools; the change is saved the moment the switch is flipped. The list
// comes straight from the screen registry, so a screen added later shows up here
// without any change.
function SettingsPage() {
  const canManage = useCanManageSettings();
  const { live, loading } = useScreenPlacement();
  const { toast, setToast, showToast } = useToast();
  const [busyKey, setBusyKey] = useState(null);

  if (!canManage) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }} data-testid="settings-denied">
        You do not have access to Settings.
      </div>
    );
  }

  const handleToggle = async (screen, makeLive) => {
    if (busyKey) return;
    setBusyKey(screen.key);
    try {
      const res = await moveScreens([screen.key], makeLive ? 'live' : 'standard');
      setPlacement(res.live);
      showToast(
        'success',
        makeLive ? 'Moved To Live' : 'Moved Back',
        makeLive
          ? `${screen.label} now appears in Supporting Tools Live.`
          : `${screen.label} now appears in Supporting Tools.`,
      );
    } catch (err) {
      showToast('error', 'Update Failed', apiErrorMessage(err, `Could not update ${screen.label}.`), true);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div data-testid="supporting-tools-settings">
      <Toast toast={toast} onClose={() => setToast(null)} testId="settings-toast" />

      <div style={{ marginBottom: '16px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Settings</h2>
        <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0' }}>
          Turn on Make It Live to move a screen to Supporting Tools Live. Turn it off to bring it back to Supporting Tools.
        </p>
      </div>

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ ...ROW, background: '#F9FAFB', fontWeight: '700', justifyContent: 'space-between' }}>
          <span>Screens ({MODULES.length})</span>
          <span style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>{live.size} Live</span>
        </div>
        {loading && <div style={{ ...ROW, color: '#9CA3AF' }}>Loading…</div>}
        {!loading && MODULES.map((m) => (
          <div key={m.key} style={{ ...ROW, background: live.has(m.key) ? '#F0FDF4' : 'transparent' }} data-testid={`settings-row-${m.key}`}>
            <span style={{ flex: 1, minWidth: 0 }}>{m.label}</span>
            <Switch
              checked={live.has(m.key)}
              disabled={busyKey !== null}
              onChange={(e) => handleToggle(m, e.target.checked)}
              label="Make It Live"
              testId={`settings-live-switch-${m.key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default SettingsPage;
